import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AdminPage from '../../components/admin/AdminPage';
import SectionCard from '../../components/admin/SectionCard';
import MetricCard from '../../components/admin/MetricCard';
import { LoadingState } from '../../components/admin/StateViews';
import { AdminSettings, ImpactStory } from '../../types';
import { AdminSettingsService } from '../../services/AdminSettingsService';
import { ImpactStoryService } from '../../services/ImpactStoryService';
import { AuditLogService } from '../../services/AuditLogService';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../context/ToastContext';

const ContentControlPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [settings, setSettings] = useState<AdminSettings | null>(null);
  const [stories, setStories] = useState<ImpactStory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [settingsResult, storiesResult] = await Promise.all([
          AdminSettingsService.getSettings(),
          ImpactStoryService.getImpactStories().catch(() => []),
        ]);
        setSettings(settingsResult);
        setStories(storiesResult);
      } catch (loadError) {
        console.error(loadError);
        setError('Failed to load content controls.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleFeatureToggle = async (
    feature: keyof AdminSettings['featureFlags'],
    value: boolean
  ) => {
    if (!settings) {
      return;
    }

    const nextSettings = {
      ...settings,
      featureFlags: {
        ...settings.featureFlags,
        [feature]: value,
      },
    };

    setSettings(nextSettings);
    await AdminSettingsService.saveSettings(nextSettings);
    await AuditLogService.recordEntry({
      module: 'content',
      action: 'feature_flag_changed',
      description: `Content feature flag "${feature}" changed to ${value ? 'enabled' : 'disabled'}.`,
      actorEmail: user?.email || 'admin@local',
    });
    showToast('Content controls updated.');
  };

  if (loading) {
    return <LoadingState label="Loading content controls…" />;
  }

  return (
    <AdminPage
      title="Content Control"
      description="Manage visibility switches and editorial content from the same admin surface."
      action={
        <Button variant="contained" onClick={() => navigate('/content/stories')}>
          Manage Stories
        </Button>
      }
    >
      {error ? <Alert severity="error">{error}</Alert> : null}

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <MetricCard
            label="Impact stories"
            value={String(stories.length)}
            helperText="Editorial items currently available"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            label="Featured causes"
            value={settings?.featureFlags.featuredCauses ? 'On' : 'Off'}
            helperText="Homepage merchandising"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            label="Audit feed"
            value={settings?.featureFlags.adminAuditFeed ? 'On' : 'Off'}
            helperText="Operational visibility for admin actions"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={5}>
          <SectionCard
            title="Feature visibility"
            description="These toggles are stored locally until a dedicated settings backend is connected."
          >
            <Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(settings?.featureFlags.featuredCauses)}
                    onChange={(event) =>
                      handleFeatureToggle('featuredCauses', event.target.checked)
                    }
                  />
                }
                label="Enable featured causes"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(settings?.featureFlags.impactStories)}
                    onChange={(event) =>
                      handleFeatureToggle('impactStories', event.target.checked)
                    }
                  />
                }
                label="Enable impact stories"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(settings?.featureFlags.analyticsExports)}
                    onChange={(event) =>
                      handleFeatureToggle('analyticsExports', event.target.checked)
                    }
                  />
                }
                label="Enable analytics exports"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(settings?.featureFlags.adminAuditFeed)}
                    onChange={(event) =>
                      handleFeatureToggle('adminAuditFeed', event.target.checked)
                    }
                  />
                }
                label="Enable admin audit feed"
              />
            </Stack>
          </SectionCard>
        </Grid>
        <Grid item xs={12} lg={7}>
          <SectionCard
            title="Recent impact stories"
            description="Stories continue to use the same admin CRUD pattern already present in the app."
            action={
              <Button variant="outlined" onClick={() => navigate('/content/stories/new')}>
                Create Story
              </Button>
            }
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stories.slice(0, 5).map((story) => (
                  <TableRow
                    key={story.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/content/stories/${story.id}/edit`)}
                  >
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography fontWeight={600}>{story.title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {story.story.slice(0, 84)}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {new Date(story.updatedAt || story.createdAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </SectionCard>
        </Grid>
      </Grid>
    </AdminPage>
  );
};

export default ContentControlPage;
